import type { MutationInfo } from '../types/observations';
import { generateSelector } from '../utils/selector-utils';
import { Logger } from '../core/logger';

export type MutationCallback = (mutations: MutationInfo[]) => void;

export class MutationWatcher {
  private observer: MutationObserver | null = null;
  private callbacks: Set<MutationCallback> = new Set();
  private logger: Logger;
  private isWatching = false;

  constructor(logger: Logger) {
    this.logger = logger;
  }

  /**
   * Start watching for DOM mutations
   */
  start(): void {
    if (this.isWatching) {
      return;
    }

    this.logger.debug('Starting mutation watcher');

    this.observer = new MutationObserver((mutations) => {
      const mutationInfos = this.processMutations(mutations);
      if (mutationInfos.length > 0) {
        this.notifyCallbacks(mutationInfos);
      }
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'disabled', 'hidden', 'value'],
      characterData: false
    });

    this.isWatching = true;
  }

  /**
   * Stop watching for mutations
   */
  stop(): void {
    if (this.observer) {
      this.logger.debug('Stopping mutation watcher');
      this.observer.disconnect();
      this.observer = null;
      this.isWatching = false;
    }
  }

  /**
   * Add a callback for mutations
   */
  on(callback: MutationCallback): void {
    this.callbacks.add(callback);
  }

  /**
   * Remove a callback
   */
  off(callback: MutationCallback): void {
    this.callbacks.delete(callback);
  }

  /**
   * Check if watching
   */
  isActive(): boolean {
    return this.isWatching;
  }

  /**
   * Process raw mutations into MutationInfo objects
   */
  private processMutations(mutations: MutationRecord[]): MutationInfo[] {
    const mutationInfos: MutationInfo[] = [];
    const timestamp = Date.now();

    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        // Process added nodes
        mutation.addedNodes.forEach(node => {
          if (node instanceof Element && this.isSignificantElement(node)) {
            mutationInfos.push({
              type: 'added',
              selector: this.getSafeSelector(node),
              timestamp
            });
          }
        });

        // Process removed nodes
        mutation.removedNodes.forEach(node => {
          if (node instanceof Element && this.isSignificantElement(node)) {
            mutationInfos.push({
              type: 'removed',
              selector: undefined, // Can't generate selector for removed element
              timestamp
            });
          }
        });
      } else if (mutation.type === 'attributes') {
        const target = mutation.target;
        if (target instanceof Element && this.isSignificantElement(target)) {
          mutationInfos.push({
            type: 'modified',
            selector: this.getSafeSelector(target),
            timestamp
          });
        }
      }
    }

    return mutationInfos;
  }

  /**
   * Check if element is significant enough to report
   */
  private isSignificantElement(element: Element): boolean {
    // Ignore script, style, and meta tags
    const ignoredTags = ['SCRIPT', 'STYLE', 'META', 'LINK', 'NOSCRIPT'];
    if (ignoredTags.includes(element.tagName)) {
      return false;
    }

    // Ignore elements added by the SDK itself
    if (element.classList.contains('eazy-navigator-overlay') ||
        element.getAttribute('data-eazy-navigator') === 'true') {
      return false;
    }

    return true;
  }

  /**
   * Safely generate a selector for an element
   */
  private getSafeSelector(element: Element): string | undefined {
    try {
      return generateSelector(element);
    } catch (error) {
      this.logger.error('Failed to generate selector for mutated element', error);
      return undefined;
    }
  }

  /**
   * Notify all callbacks
   */
  private notifyCallbacks(mutations: MutationInfo[]): void {
    this.callbacks.forEach(callback => {
      try {
        callback(mutations);
      } catch (error) {
        this.logger.error('Error in mutation callback', error);
      }
    });
  }
}
